// Detailseite eines gesperrten Talents (Talentspeicher, D9) und der
// Platzhalter für Ergebnisse, solange der Speicher noch geprüft wird.
//
// Gesperrt ist ein Talent ab Rang N+1 im Talentspeicher, nur in enforced. Das
// entscheidet useTalentLock auf der Seite. Sichtbar bleiben Name, Stelle,
// Datum, Status, Kommentare und die Löschfrist. Score, Zusatzangaben,
// Reports, PDF, Dokumente, Verlauf und Kreuzmatching werden gar nicht erst
// gemountet. So steht kein echter Wert im DOM, und keine Query lädt sie.
// Aktionen sind Status setzen und Löschen (DSGVO), dazu mehr Speicher
// anfragen und der Weg zur Talentliste.
import { LockClosedIcon, TrashIcon } from '@heroicons/react/20/solid'
import { CheckCircleIcon } from '@heroicons/react/24/outline'
import PropTypes from 'prop-types'
import { useEffect, useId, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'

import { useLanguageContext } from '../../context'
import {
  TALENT_LOCK_STATUS,
  useEntitlements,
  useTalentStorage
} from '../../features/pricing/hooks'
import {
  PRICING_REQUEST_STATUS,
  usePricingRequest
} from '../../features/pricing/use-pricing-request'
import { toBase64 } from '../../helpers'
import { localStorage } from '../../helpers/local-storage'
import { getPrimaryTalentStatus } from '../../helpers/spaces-table-data'
import { getTalentStatusText } from '../../helpers/talent-statuses'
import { LOCKED_TALENTS_FILTER } from '../../helpers/talent-storage-table'
import { describeLimitDialog } from '../pricing/limit-dialog'
import { formatCount } from '../pricing/usage-meter'
import Button from '../tailwind/Button'
import PageHeading from '../tailwind/PageHeading'
import SpaceActionsDropdown, {
  LOCKED_TALENT_ACTIONS
} from './space-actions-dropdown'
import SpaceComment from './space-comment'
import SpaceHeader from './space-header'
import SpaceTtlTimeline from './space-ttl-timeline'
import TalentSearchCombobox from './TalentSearchCombobox'

export const TALENT_DETAIL_VIEWS = Object.freeze({
  FULL: 'full',
  PENDING: 'pending',
  LOCKED: 'locked'
})

/**
 * Variante der Detailseite aus dem Ergebnis von useTalentLock. pending gibt
 * es nur vorläufig (Entitlements nicht settled) oder in enforced, solange die
 * Talents-Query noch nicht gezählt hat. legacy, preview und ein Query-Fehler
 * ergeben unlocked und damit die volle Seite (fail-open).
 *
 * @param {{ status?: string }|null} lock
 * @returns {'full'|'pending'|'locked'}
 */
export const resolveTalentDetailView = (lock) => {
  if (lock?.status === TALENT_LOCK_STATUS.LOCKED) {
    return TALENT_DETAIL_VIEWS.LOCKED
  }
  if (lock?.status === TALENT_LOCK_STATUS.PENDING) {
    return TALENT_DETAIL_VIEWS.PENDING
  }
  return TALENT_DETAIL_VIEWS.FULL
}

const DELETE_JOB = 'AUTOMATION_DELETE_SPACE_CONNECTION_TO_PARTNER'

/**
 * Nächste automatische Löschung aus der DSGVO-Automatisierung. ttl steht in
 * Sekunden. Vergangene Termine zählen nicht, ohne Termin null.
 *
 * @param {Array<{ job: string, ttl: number }>} cronTtls
 * @param {number} [now]
 * @returns {Date|null}
 */
export const nextDeletionDate = (cronTtls, now = Date.now()) => {
  const times = (Array.isArray(cronTtls) ? cronTtls : [])
    .filter((item) => item?.job === DELETE_JOB)
    .map((item) => Number(item.ttl) * 1000)
    .filter((time) => Number.isFinite(time) && time >= now)

  return times.length ? new Date(Math.min(...times)) : null
}

/** Datum im Format der Oberfläche, ohne gültiges Datum leer */
export const formatDate = (value, language) => {
  if (value === null || value === undefined || value === '') return ''
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return ''

  const locale = String(language || '')
    .toLowerCase()
    .startsWith('en')
    ? 'en-US'
    : 'de-DE'

  return date.toLocaleDateString(locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  })
}

// Titel und Text nach einer Anfrage, wie in UpgradeInline
const DONE_TEXTS = {
  [PRICING_REQUEST_STATUS.SENT]: {
    title: 'pricing.dialog.sent_title',
    text: 'pricing.dialog.sent_text'
  },
  [PRICING_REQUEST_STATUS.DUPLICATE]: {
    title: 'pricing.dialog.already_sent_title',
    text: 'pricing.dialog.already_sent_text'
  },
  [PRICING_REQUEST_STATUS.DRY_RUN]: {
    title: 'pricing.dialog.admin_dry_run_title',
    text: 'pricing.dialog.admin_dry_run_text'
  }
}

const REQUEST_SOURCE = 'talent_detail_locked'

const TALENTS_PATH = '/talents'

/**
 * Link auf die Talentliste mit dem Filter „Gesperrt", als computed-Parameter
 * wie die Filter-Links im Limit-Dialog
 */
export const lockedTalentsLocation = () => ({
  pathname: TALENTS_PATH,
  search: `computed=${encodeURIComponent(toBase64(LOCKED_TALENTS_FILTER))}`
})

/**
 * Anfrage „Mehr Speicher" wie im Limit-Dialog. Mehr Speicher kommt über
 * Zusatzstellen, am Stellenmaximum des Pakets bleibt nur ein größeres Paket.
 */
const useStorageRequest = () => {
  const entitlements = useEntitlements()
  const storage = useTalentStorage()

  const { requestOption } = describeLimitDialog({
    resource: 'talentStorage',
    action: 'info',
    state: storage.state,
    used: storage.used,
    limit: storage.limit,
    planId: entitlements.plan?.id ?? null,
    careersLimit: entitlements.limits?.careers?.limit ?? null
  })

  const request = usePricingRequest({
    kind: requestOption.kind,
    key: requestOption.key,
    targetPlan: requestOption.targetPlan,
    source: REQUEST_SOURCE,
    usage: {
      resource: 'talentStorage',
      used: storage.used,
      limit: storage.limit
    }
  })

  return { request, upgrade: requestOption.upgrade }
}

const MetaItem = ({ label, children }) => (
  <div className='min-w-0'>
    <dt className='text-sm text-gray-500'>{label}</dt>
    <dd className='mt-1 truncate text-sm font-medium text-gray-900'>
      {children}
    </dd>
  </div>
)

MetaItem.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired
}

const careerTitleOf = (t, career) => {
  if (!career?.title) return null

  const title = career.archived
    ? t('talents.career_archived_title', { career_title: career.title })
    : career.title

  return career.external_custom_id
    ? `${title} | ${career.external_custom_id}`
    : title
}

const TalentLockedView = ({
  space,
  career,
  refetchSpace,
  spaceActions,
  rank,
  limit,
  adminPanel
}) => {
  const { t, i18n } = useTranslation()
  const { language } = useLanguageContext()
  const navigate = useNavigate()
  const titleId = useId()
  const statusRef = useRef(null)
  // Fokus nur nach eigenem Klick auf den Status setzen, wie in UpgradeInline
  const requestedHereRef = useRef(false)

  const { request, upgrade } = useStorageRequest()
  const done = DONE_TEXTS[request.status] || null

  useEffect(() => {
    if (done && requestedHereRef.current) statusRef.current?.focus()
  }, [done])

  const handleRequest = () => {
    requestedHereRef.current = true
    request.send()
  }

  // Die Talentliste liest den gespeicherten Filter beim Mounten
  const goToLockedTalents = () => {
    const location = lockedTalentsLocation()
    localStorage.setItem(location.pathname, location.search)
    navigate(location)
  }

  const uiLanguage = i18n?.language
  const careerTitle = careerTitleOf(t, career)
  const invitedAt = formatDate(space.createdAt, uiLanguage)
  const { status: talentStatus } = getPrimaryTalentStatus(space)
  const statusText =
    getTalentStatusText({
      status: talentStatus,
      t,
      language,
      progress: space.progress
    }) || t('pages.talent.status_empty')
  const deletionDate = nextDeletionDate(space.cron_ttls)

  return (
    <>
      <PageHeading
        withActions={
          <div className='ml-auto flex items-center justify-center gap-x-3'>
            <TalentSearchCombobox currentSpaceId={space.id} />
            <SpaceActionsDropdown
              space={space}
              refetchSpace={refetchSpace}
              career={career}
              spaceActions={spaceActions}
              restrictedActions={LOCKED_TALENT_ACTIONS}
            />
          </div>
        }
        sticky
      >
        <SpaceHeader space={space} />
      </PageHeading>
      {adminPanel}
      <div className='mb-8 mt-8 min-h-full px-4 sm:px-6 lg:px-8'>
        <section
          aria-labelledby={titleId}
          data-storage-locked='true'
          className='mx-auto max-w-2xl rounded-lg bg-white p-6 ring-1 ring-gray-200 sm:p-8'
        >
          <div className='mx-auto flex size-12 items-center justify-center rounded-full bg-gray-100'>
            <LockClosedIcon
              aria-hidden='true'
              className='size-6 text-gray-500'
            />
          </div>
          <h2
            id={titleId}
            className='mt-4 text-center text-lg font-semibold text-gray-900'
          >
            {t('pricing.talent_locked.detail_title')}
          </h2>
          <p className='mt-2 text-center text-sm text-gray-600'>
            {t('pricing.talent_locked.detail_text', {
              limit: formatCount(limit, uiLanguage),
              rank: formatCount(rank, uiLanguage)
            })}
          </p>
          <p className='mt-1 text-center text-sm text-gray-500'>
            {t('pricing.talent_locked.detail_rule')}
          </p>

          <dl className='mt-6 grid grid-cols-1 gap-4 border-t border-gray-100 pt-6 sm:grid-cols-3'>
            {careerTitle && (
              <MetaItem label={t('pages.talent.space_details_career_label')}>
                <Link
                  to={`/career/${career.id}`}
                  title={careerTitle}
                  className='text-primaryBlue hover:text-primaryBlue/80'
                >
                  {careerTitle}
                </Link>
              </MetaItem>
            )}
            {invitedAt && (
              <MetaItem label={t('pricing.talent_locked.meta_invited_at')}>
                {invitedAt}
              </MetaItem>
            )}
            <MetaItem label={t('pages.talent.status_eyebrow')}>
              {statusText}
            </MetaItem>
          </dl>

          <div className='mt-6 border-t border-gray-100 pt-6'>
            {done && (
              <div
                ref={statusRef}
                tabIndex={-1}
                role='status'
                className='mb-4 flex items-start justify-center gap-x-2 rounded focus:outline-none'
              >
                <CheckCircleIcon
                  aria-hidden='true'
                  className='size-5 shrink-0 text-green-600'
                />
                <div className='text-sm'>
                  <p className='font-semibold text-gray-900'>{t(done.title)}</p>
                  <p className='mt-0.5 text-gray-600'>{t(done.text)}</p>
                </div>
              </div>
            )}
            <div className='flex flex-wrap items-center justify-center gap-3'>
              {!done && (
                <Button.PrimaryLG
                  text={
                    upgrade
                      ? t('pricing.limit.option_upgrade_title')
                      : t('pricing.banner.request_more_action')
                  }
                  onClick={handleRequest}
                  isLoading={request.isSending}
                />
              )}
              <Button.SecondaryLG
                text={t('pricing.talent_locked.manage_action')}
                onClick={goToLockedTalents}
              />
            </div>
            {request.isError && (
              <p role='alert' className='mt-2 text-center text-sm text-red-600'>
                {t('pricing.dialog.error_text')}
              </p>
            )}
            <p className='sr-only' aria-live='polite'>
              {request.isSending ? t('pricing.dialog.sending_text') : ''}
            </p>
          </div>

          <div className='mt-6 rounded-md bg-gray-50 p-4 text-center'>
            <p className='text-sm text-gray-600'>
              {t('pricing.talent_locked.detail_delete_hint')}
            </p>
            {deletionDate && (
              <p className='mt-1 text-xs text-gray-500'>
                {t('pricing.talent_locked.deletion_date', {
                  date: formatDate(deletionDate, uiLanguage)
                })}
              </p>
            )}
            <Button.WarningText
              text={t('pricing.talent_locked.delete_action')}
              icon={<TrashIcon aria-hidden='true' className='size-4' />}
              minWidth='min-w-0'
              className='mt-3'
              onClick={() => spaceActions.setDisplayDeleteSpace(true)}
            />
          </div>
        </section>

        <div className='mx-auto mt-8 max-w-2xl space-y-4'>
          {!!space.cron_ttls?.length && <SpaceTtlTimeline space={space} />}
          <SpaceComment data={{ space, refetchSpace }} />
        </div>
      </div>
    </>
  )
}

TalentLockedView.propTypes = {
  space: PropTypes.object.isRequired,
  career: PropTypes.object,
  refetchSpace: PropTypes.func.isRequired,
  spaceActions: PropTypes.object.isRequired,
  rank: PropTypes.number,
  limit: PropTypes.number,
  // Admin-Buttons der Seite, nur für isAdmin
  adminPanel: PropTypes.node
}

TalentLockedView.defaultProps = {
  career: null,
  rank: null,
  limit: null,
  adminPanel: null
}

const SkeletonPanel = () => (
  <div
    aria-hidden='true'
    className='w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-5 sm:p-6'
  >
    <div className='h-4 w-1/3 animate-pulse rounded bg-gray-200' />
    <div className='mt-2 h-3 w-1/2 animate-pulse rounded bg-gray-200' />
    <div className='mt-5 space-y-3 border-t border-gray-300 pt-5'>
      <div className='h-3 w-full animate-pulse rounded bg-gray-200' />
      <div className='h-3 w-5/6 animate-pulse rounded bg-gray-200' />
      <div className='h-3 w-2/3 animate-pulse rounded bg-gray-200' />
    </div>
  </div>
)

/**
 * Platzhalter für die Ergebnisbereiche, solange der Talentspeicher prüft. Er
 * enthält keine Daten des Talents. announce setzt einmal je Seite einen
 * Ladehinweis für Screenreader. Er ist schlichter Text und keine Live-Region,
 * denn eine Region, die schon mit Inhalt eingefügt wird, sagen Screenreader
 * meist nicht an.
 */
export const TalentResultsSkeleton = ({ panels, announce }) => {
  const { t } = useTranslation()

  return (
    <div className='relative space-y-4' data-storage-pending='true'>
      {announce && (
        <p className='sr-only'>{t('pricing.talent_locked.pending_sr')}</p>
      )}
      {Array.from({ length: panels }, (_, index) => (
        <SkeletonPanel key={index} />
      ))}
    </div>
  )
}

TalentResultsSkeleton.propTypes = {
  panels: PropTypes.number,
  announce: PropTypes.bool
}

TalentResultsSkeleton.defaultProps = {
  panels: 2,
  announce: false
}

export default TalentLockedView
